import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { H3 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, useCallback, useEffect, useState } from 'react';

import { CatalogDate } from '@/components/CatalogComponent/components/Details/components/CatalogDate/CatalogDate';
import { RequestOnlineBiddingButton } from '@/components/ItemBidding/RequestOnlineBiddingButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogIsCatalogOnly,
    getCatalogIsDone,
    getCatalogSellerName,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogDetailExternalUrl } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { submitRegisterForCatalog } from '@/redux/modules/registerForCatalog';
import { useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics/registration/trackRegisterForAuctionPressed.analytics';

import { RegistrationStatusLabel } from './RegistrationStatusLabel';

type Props = {
    catalogId: number;
    show: boolean;
};

const RegisterAuctionBannerComponent = ({ catalogId, show }: Props) => {
    const dispatch = useAppDispatch();

    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const isDone = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const name = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const externalUrl = useAppSelector((state) => getCatalogDetailExternalUrl(state, catalogId));
    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        if (show) {
            setShouldRender(true);
            // setTimeout ensures the initial hidden state is painted before transitioning
            setTimeout(() => {
                setIsVisible(true);
            }, 50);
        } else {
            setIsVisible(false);
            // Wait for transition to complete before unmounting
            const timeout = setTimeout(() => {
                setShouldRender(false);
            }, 300);
            return () => clearTimeout(timeout);
        }
    }, [show]);

    const registerForCatalog = useCallback(
        (args) => {
            trackRegisterForAuctionPressedAnalytics();
            dispatch(submitRegisterForCatalog(args));
        },
        [dispatch, trackRegisterForAuctionPressedAnalytics]
    );

    if (!shouldRender) {
        return null;
    }

    return (
        <div
            className={cn(
                // GENERAL
                'fixed left-0 top-0 z-[1000] flex w-full justify-center bg-background-primary shadow-01',
                // ANIMATION
                'transition-all duration-300 ease-out',
                isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
            )}
        >
            <div className="flex w-full max-w-dense items-center justify-between self-center px-[20px] py-sm">
                <div className="flex flex-col items-start justify-center">
                    <H3 className="mb-xs line-clamp-1 text-ellipsis">
                        <FormattedMessage
                            id="bannerTitle"
                            values={{
                                name,
                                title,
                            }}
                        />
                    </H3>
                    <CatalogDate
                        catalogId={catalogId}
                        hideBorders
                    />
                </div>
                {isCatalogOnly ? (
                    <div>
                        <RequestOnlineBiddingButton
                            catalogId={catalogId}
                            submitRegisterForCatalog={registerForCatalog}
                            text={Boolean(externalUrl) && <FormattedMessage id="visit_partners_site" />}
                        />
                    </div>
                ) : !isDone ? (
                    <RegistrationStatusLabel catalogId={catalogId} />
                ) : null}
            </div>
        </div>
    );
};

export const RegisterAuctionBanner = memo<Props>(RegisterAuctionBannerComponent);
